import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Package, Server, Database, HardDrive, Search, Clock, Loader2, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Fold } from "../../components/admin/Section";

function Dot({ ok, pending }) {
  const cls = pending ? "bg-amber-500" : ok ? "bg-emerald-500" : "bg-red-500";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} data-testid="sh-dot" />;
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtUptime(s) {
  if (!s && s !== 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d) return `${d}g ${h}sa ${m}dk`;
  if (h) return `${h}sa ${m}dk`;
  if (m) return `${m}dk ${sec}sn`;
  return `${sec}sn`;
}

const STATUS_STYLES = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  patch: "bg-sky-50 text-sky-700 border-sky-200",
  minor: "bg-amber-50 text-amber-700 border-amber-200",
  major: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABELS = { ok: "Güncel", patch: "Yama", minor: "Minör", major: "Majör" };

function PkgList({ list, filter }) {
  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = q ? list.filter((p) => p.name.toLowerCase().includes(q)) : list;
    return [...base].sort((a, b) => {
      const order = { major: 0, minor: 1, patch: 2, ok: 3 };
      const d = (order[a.status] ?? 9) - (order[b.status] ?? 9);
      if (d) return d;
      return a.name.localeCompare(b.name);
    });
  }, [list, filter]);
  return (
    <div className="border rounded-lg divide-y max-h-[480px] overflow-y-auto" data-testid="sh-pkg-list">
      {items.map((p) => (
        <div key={p.name} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-3 py-2 text-sm" data-testid={`sh-pkg-${p.name}`}>
          <div className="truncate">
            <span className="font-medium">{p.name}</span>
            {p.direct && <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400">doğrudan</span>}
          </div>
          <code className="text-xs text-stone-500">{p.current || "—"}</code>
          <code className="text-xs text-stone-500">→ {p.latest || "—"}</code>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.ok}`}>{STATUS_LABELS[p.status] || p.status}</span>
        </div>
      ))}
      {!items.length && <p className="px-3 py-6 text-center text-sm text-stone-400">Eşleşen paket yok.</p>}
    </div>
  );
}

function SummaryChip({ label, value, tone = "stone", testid }) {
  const map = {
    stone: "bg-stone-50 border-stone-200 text-stone-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${map[tone]}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}

export default function SystemHealth() {
  const [filter, setFilter] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const health = useQuery({
    queryKey: ["admin", "sys-health"],
    queryFn: () => api.get("/admin/system/health").then((r) => r.data),
    refetchInterval: 15000,
  });
  const pkgs = useQuery({
    queryKey: ["admin", "sys-packages", refreshToken],
    queryFn: () => api.get(`/admin/system/packages${refreshToken ? "?refresh=true" : ""}`).then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });

  const h = health.data || {};
  const s = pkgs.data?.summary || { backend: {}, frontend: {} };
  const q = filter.trim().toLowerCase();
  const beList = pkgs.data?.backend || [];
  const feList = pkgs.data?.frontend || [];
  const beMatches = q ? beList.filter((p) => p.name.toLowerCase().includes(q)).length : beList.length;
  const feMatches = q ? feList.filter((p) => p.name.toLowerCase().includes(q)).length : feList.length;

  // Build quick lookups so the status cards can show "current → latest".
  const beBy = useMemo(() => Object.fromEntries(beList.map((p) => [p.name.toLowerCase(), p])), [beList]);
  const feBy = useMemo(() => Object.fromEntries(feList.map((p) => [p.name.toLowerCase(), p])), [feList]);
  const ver = (map, name) => {
    const p = map[name.toLowerCase()];
    if (!p) return null;
    return { current: p.current, latest: p.latest, status: p.status };
  };
  const Line = ({ label, current, latest, status, mono = true }) => {
    const outdated = latest && current && latest !== current && status && status !== "ok";
    return (
      <div className="flex justify-between items-baseline gap-2">
        <dt className="shrink-0">{label}</dt>
        <dd className={`truncate text-right ${mono ? "font-mono" : ""}`}>
          <span className={outdated ? "text-amber-700 font-semibold" : ""}>{current || "—"}</span>
          {outdated ? (
            <span className="ml-1 text-[10px] text-emerald-700 font-mono">→ {latest}</span>
          ) : null}
        </dd>
      </div>
    );
  };
  const fastapi = ver(beBy, "fastapi");
  const pydantic = ver(beBy, "pydantic");
  const motorV = ver(beBy, "motor");
  const uvicorn = ver(beBy, "uvicorn");
  const reactV = ver(feBy, "react");
  const reactDom = ver(feBy, "react-dom");

  return (
    <div className="space-y-5" data-testid="settings-system-health">
      {/* Top status cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4" data-testid="sh-card-server">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Server size={15} />Sunucu</div>
            <Dot ok={!health.isError} pending={health.isLoading} />
          </div>
          <dl className="text-xs space-y-1 text-stone-600">
            <div className="flex justify-between"><dt>Python</dt><dd className="font-mono">{h.server?.python || "—"}</dd></div>
            <div className="flex justify-between"><dt>Node</dt><dd className="font-mono">{h.server?.node || "—"}</dd></div>
            <div className="flex justify-between"><dt>Platform</dt><dd className="truncate ml-2">{h.server?.platform || "—"}</dd></div>
            <div className="flex justify-between"><dt>Uptime</dt><dd>{fmtUptime(h.server?.uptime_sec)}</dd></div>
          </dl>
        </div>

        <div className="bg-white border rounded-xl p-4" data-testid="sh-card-runtime">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Package size={15} />Çekirdek Kütüphaneler</div>
            <Dot ok={!!h.runtime?.fastapi} pending={health.isLoading} />
          </div>
          <dl className="text-xs space-y-1 text-stone-600">
            <div className="flex justify-between"><dt>FastAPI</dt><dd className="font-mono">{h.runtime?.fastapi || "—"}</dd></div>
            <div className="flex justify-between"><dt>Pydantic</dt><dd className="font-mono">{h.runtime?.pydantic || "—"}</dd></div>
            <div className="flex justify-between"><dt>Motor</dt><dd className="font-mono">{h.runtime?.motor || "—"}</dd></div>
            <div className="flex justify-between"><dt>Uvicorn</dt><dd className="font-mono">{h.runtime?.uvicorn || "—"}</dd></div>
          </dl>
        </div>

        <div className="bg-white border rounded-xl p-4" data-testid="sh-card-db">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Database size={15} />MongoDB</div>
            <Dot ok={!!h.database?.ok} pending={health.isLoading} />
          </div>
          <dl className="text-xs space-y-1 text-stone-600">
            <div className="flex justify-between"><dt>Sürüm</dt><dd className="font-mono">{h.database?.version || "—"}</dd></div>
            <div className="flex justify-between"><dt>DB</dt><dd className="font-mono truncate ml-2">{h.database?.db || "—"}</dd></div>
            <div className="flex justify-between"><dt>Koleksiyonlar</dt><dd>{h.database?.collections ?? "—"}</dd></div>
            <div className="flex justify-between"><dt>Depolama</dt><dd>{fmtBytes(h.database?.storage_bytes)}</dd></div>
          </dl>
        </div>

        <div className="bg-white border rounded-xl p-4" data-testid="sh-card-storage">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><HardDrive size={15} />Depolama & Entegrasyon</div>
            <Dot ok={!!h.storage?.ok} pending={health.isLoading} />
          </div>
          <dl className="text-xs space-y-1 text-stone-600">
            <div className="flex justify-between"><dt>Object Storage</dt><dd>{h.storage?.ok ? "Aktif" : "Yok"}</dd></div>
            <div className="flex justify-between"><dt>E-posta (Resend)</dt><dd>{h.integrations?.email?.configured ? "Aktif" : "Kapalı"}</dd></div>
            <div className="flex justify-between"><dt>Telegram botu</dt><dd>{h.integrations?.telegram?.configured ? "Aktif" : "Kapalı"}</dd></div>
            <div className="flex justify-between"><dt>Yapay zeka</dt><dd>{h.integrations?.ai?.configured ? h.integrations.ai.provider : "Kapalı"}</dd></div>
          </dl>
        </div>
      </div>

      {/* Calendar + Telegram sessions */}
      {h.calendar && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryChip label="Takvim • toplam" value={h.calendar.events_total} testid="sh-ev-total" />
          <SummaryChip label="Takvim • yaklaşan" value={h.calendar.events_upcoming} tone="sky" testid="sh-ev-upcoming" />
          <SummaryChip label="Telegram oturumu" value={h.calendar.telegram_sessions} tone="emerald" testid="sh-tg-sessions" />
          <SummaryChip label="Son güncellenme" value={h.generated_at ? new Date(h.generated_at).toLocaleTimeString("tr-TR") : "—"} testid="sh-updated" />
        </div>
      )}

      {/* Packages section */}
      <div className="bg-white border rounded-xl p-5" data-testid="sh-packages">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2"><Package size={16} />Paket Durumu</h3>
            <p className="text-sm text-stone-500">Yüklü sürümler ile npm/pypi'deki son sürümler karşılaştırılır. Önbellek 1 saat.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setRefreshToken((t) => t + 1)} disabled={pkgs.isFetching} data-testid="sh-pkg-refresh">
            <RefreshCw size={13} className={`mr-1 ${pkgs.isFetching ? "animate-spin" : ""}`} />Yenile
          </Button>
        </div>

        {pkgs.isLoading ? (
          <p className="text-sm text-stone-400 py-6 text-center">Paket listesi hazırlanıyor…</p>
        ) : pkgs.isError ? (
          <p className="text-sm text-red-500 py-6 text-center">Paket bilgisi alınamadı.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <SummaryChip label="Backend toplam" value={s.backend.total ?? 0} testid="sh-b-total" />
              <SummaryChip label="Güncel" value={s.backend.ok ?? 0} tone="emerald" testid="sh-b-ok" />
              <SummaryChip label="Yama" value={s.backend.patch ?? 0} tone="sky" testid="sh-b-patch" />
              <SummaryChip label="Minör" value={s.backend.minor ?? 0} tone="amber" testid="sh-b-minor" />
              <SummaryChip label="Majör" value={s.backend.major ?? 0} tone="red" testid="sh-b-major" />
              <SummaryChip label="Frontend toplam" value={s.frontend.total ?? 0} testid="sh-f-total" />
              <SummaryChip label="Güncel" value={s.frontend.ok ?? 0} tone="emerald" testid="sh-f-ok" />
              <SummaryChip label="Yama" value={s.frontend.patch ?? 0} tone="sky" testid="sh-f-patch" />
              <SummaryChip label="Minör" value={s.frontend.minor ?? 0} tone="amber" testid="sh-f-minor" />
              <SummaryChip label="Majör" value={s.frontend.major ?? 0} tone="red" testid="sh-f-major" />
            </div>

            <div className="relative mb-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input className="pl-9" placeholder="Paket ara…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sh-pkg-search" />
            </div>

            <Fold title={q ? `Backend (Python) • ${beMatches} / ${beList.length} eşleşme` : `Backend (Python) • ${beList.length} paket`} testid="sh-fold-backend" defaultOpen key={`be-${q ? (beMatches > 0 ? 'o' : 'c') : 'd'}`}>
              <PkgList list={beList} filter={filter} />
            </Fold>
            <Fold title={q ? `Frontend (Node) • ${feMatches} / ${feList.length} eşleşme` : `Frontend (Node) • ${feList.length} paket`} testid="sh-fold-frontend" defaultOpen={q ? feMatches > 0 && beMatches === 0 : false} key={`fe-${q ? (feMatches > 0 && beMatches === 0 ? 'o' : 'c') : 'd'}`}>
              <PkgList list={feList} filter={filter} />
            </Fold>
          </>
        )}
      </div>
    </div>
  );
}
